import styled from "styled-components";

// Shared layout primitives for the Media Studio sections (Home, Review,
// Calendar, Settings). Card-based surfaces on design-system tokens.

export const SectionRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--space-4) var(--space-4) var(--space-8);
`;

export const SectionTitle = styled.h2`
  font-family: var(--font-display);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-text);
  margin: 0;
`;

export const SectionSub = styled.p`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.5;
`;

export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--space-3);
`;

export const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  border-radius: var(--radius-xl);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  box-shadow: var(--shadow-xs);
`;

export const CardTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  font-family: var(--font-display);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

export const CardHint = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  line-height: 1.5;
`;

export const BigCount = styled.div`
  font-family: var(--font-display);
  font-size: 34px;
  font-weight: var(--font-weight-bold);
  color: var(--color-text);
  line-height: 1;
`;

export const RowList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: 8px 0;
  border-top: 1px solid var(--color-border-subtle);

  &:first-child {
    border-top: none;
  }
`;

export const StatusDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  flex-shrink: 0;
`;

export const Thumb = styled.img`
  width: 32px;
  height: 40px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;
  background: var(--color-surface-subtle);
`;

export const Muted = styled.span`
  font-family: var(--font-sans);
  font-size: 11px;
  color: var(--color-text-secondary);
`;

export const Spacer = styled.span`
  flex: 1 1 auto;
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-8) var(--space-4);
  text-align: center;
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
`;

export const Stat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const StatValue = styled.div`
  font-family: var(--font-display);
  font-size: 24px;
  font-weight: var(--font-weight-bold);
  color: var(--color-text);
  line-height: 1.1;
`;

export const StatLabel = styled.div`
  font-family: var(--font-sans);
  font-size: 10px;
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-tertiary);
`;

export const ExternalLink = styled.a.attrs({
  target: "_blank",
  rel: "noopener noreferrer",
})`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-info);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export const STATUS_COLOR: Record<string, string> = {
  draft: "var(--color-text-tertiary)",
  scheduled: "var(--color-info)",
  publishing: "var(--color-warning, #d0a020)",
  sent: "var(--color-success)",
  partial: "var(--color-warning, #d0a020)",
  error: "var(--color-danger)",
  cancelled: "var(--color-text-tertiary)",
};

// First frame (carousel) or poster (reel) per project, so a reviewer sees
// what a draft actually looks like before approving it.
export const projectThumb = (p?: {
  slides?: { mediaUrl: string }[];
  thumbnailUrl?: string;
}): string | undefined => (p ? p.slides?.[0]?.mediaUrl || p.thumbnailUrl : undefined);
